type Props = {
  sceneName: string;
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onAddNote: () => void;
  onUploadImages: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onClose: () => void;
  canEdit: boolean;
  isUploading: boolean;
  uploadProgress?: { current: number; total: number };
  selectedCount?: number;
  onDeleteSelected?: () => void;
  onShowHelp?: () => void;
};

export function StoryboardToolbar({
  sceneName,
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onAddNote,
  onUploadImages,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onClose,
  canEdit,
  isUploading,
  uploadProgress,
  selectedCount = 0,
  onDeleteSelected,
  onShowHelp,
}: Props) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-surface-elevated border-b border-border flex-shrink-0">
      {/* Left: Scene name and selection info */}
      <div className="flex items-center gap-3">
        <span className="text-foreground font-copernicus text-lg">{sceneName}</span>
        <span className="text-muted text-sm">Storyboard</span>
        {selectedCount > 0 && canEdit && (
          <>
            <div className="h-4 w-px bg-border" />
            <span className="text-primary-400 text-sm font-medium">{selectedCount} selected</span>
            <button
              onClick={onDeleteSelected}
              className="flex items-center gap-1 px-2 py-1 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
              title="Delete selected (Delete key)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>
          </>
        )}
      </div>

      {/* Center: Zoom controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onZoomOut}
          className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
          title="Zoom out"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
        <button
          onClick={onZoomReset}
          className="px-3 py-1.5 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors min-w-[60px]"
          title="Reset zoom"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={onZoomIn}
          className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
          title="Zoom in"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {canEdit && (
          <>
            {/* Undo/Redo */}
            <button
              onClick={onUndo}
              disabled={!canUndo}
              className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            <button
              onClick={onRedo}
              disabled={!canRedo}
              className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>
            <div className="h-4 w-px bg-border mx-1" />
            <button
              onClick={onAddNote}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors"
              title="Add text"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Add Text</span>
            </button>
            <button
              onClick={onUploadImages}
              disabled={isUploading}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors disabled:opacity-50"
              title="Upload images"
            >
              {isUploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span>
                    {uploadProgress && uploadProgress.total > 1
                      ? `${uploadProgress.current}/${uploadProgress.total}`
                      : "Uploading..."}
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Upload</span>
                </>
              )}
            </button>
          </>
        )}
        {onShowHelp && (
          <button
            onClick={onShowHelp}
            className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
            title="Keyboard shortcuts"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        )}
        <button
          onClick={onClose}
          className="p-2 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
          title="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
